/** Calendar heatmap initialization using CalHeatmap — faithful port from jQuery */

declare const CalHeatmap: any
declare const Tooltip: any
declare const LegendLite: any
declare const CalendarLabel: any
declare const dayjs: any

export function initCalendar(): void {
  const calEl = document.getElementById('cal-heatmap')
  const archiveEl = document.getElementById('archive-heatmap')
  if (!calEl && !archiveEl) return

  // Ensure CalHeatmap is loaded
  if (typeof CalHeatmap === 'undefined') return

  const scaleMeta = document.querySelector<HTMLMetaElement>('meta[property="calendar-scale"]')
  const schemeMeta = document.querySelector<HTMLMetaElement>('meta[property="calendar-scheme"]')
  const colorMeta = document.querySelector<HTMLMetaElement>('meta[property="calendar-color"]')

  const calendarScale = scaleMeta?.content || 'year'
  const calendarScheme = schemeMeta?.content || 'null'
  const calendarColor = colorMeta?.content || 'null'

  // Build color config
  const calColor: any = { type: 'threshold', domain: [4, 6, 8] }
  const d3Colors = ['blues', 'greens', 'greys', 'oranges', 'purples', 'reds', 'bugn', 'bupu', 'gnbu', 'orrd', 'pubu', 'pubugn', 'purd', 'rdpu', 'ylgn', 'ylgnbu', 'ylorbr', 'ylorrd', 'cividis', 'inferno', 'magma', 'plasma', 'viridis', 'cubehelix', 'turbo', 'warm', 'cool', 'brbg', 'prgn', 'piyg', 'puor', 'rdbu', 'rdgy', 'rdylbu', 'rdylgn', 'spectral', 'burd', 'buylrd', 'rainbow', 'sinebow']
  if (calendarScheme !== 'null') {
    if (d3Colors.includes(calendarScheme)) {
      calColor.scheme = calendarScheme
    } else {
      calColor.range = ['#9be9a8', '#40c463', '#30a14e', '#216e39']
    }
  } else if (calendarColor !== 'null') {
    calColor.range = calendarColor
  } else {
    calColor.range = ['#9be9a8', '#40c463', '#30a14e', '#216e39']
  }

  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth() + 1
  const day = today.getDate()
  const endDay = year + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0')

  // Try /stats.json first, fallback to /stats.json/ (zola serve generates directory)
  const statsUrl = '/stats.json'

  fetch(statsUrl)
    .then(res => res.ok ? res : fetch(statsUrl + '/'))
    .then(res => res.text())
    .then(text => {
      // zola serve injects livereload script at the end — strip it
      const cleaned = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '').trim()
      return JSON.parse(cleaned)
    })
    .then(statsData => {
      console.log('[calendar] stats.json loaded, entries:', statsData.total)
      if (calEl) {
        const cal = new CalHeatmap()
        const selectors = {
          cal: '#cal-heatmap',
          legend: '#ex-ghDay-legend',
          prev: '#ex-ghDay-prev',
          next: '#ex-ghDay-next',
          today: '#ex-ghDay-today',
        }
        makeCalendar(cal, selectors, calendarScale, statsData, endDay, calColor, false)
      }
      if (archiveEl) {
        const cal2 = new CalHeatmap()
        const selectors2 = {
          cal: '#archive-heatmap',
          legend: '#ex-ghDay-legend2',
          prev: '#ex-ghDay-prev2',
          next: '#ex-ghDay-next2',
          today: '#ex-ghDay-today2',
        }
        makeCalendar(cal2, selectors2, 'day', statsData, endDay, calColor, true)
      }
    })
    .catch(err => {
      console.error('[calendar] Failed to load stats.json:', err)
    })
}

interface Selectors {
  cal: string
  legend: string
  prev: string
  next: string
  today: string
}

function makeCalendar(
  cal: any,
  selectors: Selectors,
  by: string,
  statsData: any,
  endDay: string,
  calColor: any,
  isArchive: boolean
): void {
  const options: any[] = [
    [
      Tooltip,
      {
        text: function (_date: any, value: number, dayjsDate: any) {
          if (value === 1) {
            return value + ' contribution on ' + dayjsDate.format(paras.format)
          }
          return (value ? value + ' contributions' : 'No contribution') + ' on ' + dayjsDate.format(paras.format)
        },
      },
    ],
    [
      LegendLite,
      {
        includeBlank: true,
        itemSelector: selectors.legend,
        radius: 3,
        width: 10,
        height: 10,
        gutter: 2,
      },
    ],
  ]

  let paras: any
  let startDate: Date

  if (by === 'month') {
    const xMonth = function (DateHelper: any) {
      return {
        name: 'xMonth',
        allowedDomainType: ['year'],
        rowsCount: () => 3,
        columnsCount: () => 4,
        mapping: (startTimestamp: number, endTimestamp: number) =>
          DateHelper.intervals('month', startTimestamp, DateHelper.date(endTimestamp)).map(
            (ts: number) => ({
              t: ts,
              x: DateHelper.date(ts).month() % 4,
              y: Math.floor(DateHelper.date(ts).month() / 4),
            })
          ),
        extractUnit: (ts: number) => DateHelper.date(ts).startOf('month').valueOf(),
      }
    }
    cal.addTemplates(xMonth)
    paras = {
      format: 'YYYY-MM',
      domain: {
        type: 'year',
        gutter: 5,
        label: { text: (ts: number) => dayjs(ts).format('YYYY'), align: 'middle' },
      },
      subDomain: { type: 'xMonth', width: 60, height: 36, gutter: 5, radius: 3 },
      range: 1,
    }
    startDate = new Date(dayjs(endDay).startOf('year').add(1, 'month').valueOf())
  } else if (by === 'year') {
    const xYear = function (DateHelper: any) {
      return {
        name: 'xYear',
        allowedDomainType: ['year'],
        rowsCount: () => 1,
        columnsCount: () => 1,
        mapping: (startTimestamp: number, endTimestamp: number) =>
          DateHelper.intervals('year', startTimestamp, DateHelper.date(endTimestamp)).map(
            (ts: number, index: number) => ({
              t: ts,
              x: index,
              y: 0,
            })
          ),
        extractUnit: (ts: number) => DateHelper.date(ts).startOf('year').valueOf(),
      }
    }
    cal.addTemplates(xYear)
    paras = {
      format: 'YYYY',
      domain: { type: 'year', gutter: 5, label: { text: 'YYYY', align: 'middle' } },
      subDomain: { type: 'xYear', width: 36, height: 36, gutter: 5, radius: 3 },
      range: 6,
    }
    if (!isArchive) {
      const calHeatmapEl = document.getElementById('cal-heatmap')
      if (calHeatmapEl) {
        paras.range = Math.floor(calHeatmapEl.clientWidth / 40) || 6
      }
    }
    startDate = new Date(dayjs(endDay).subtract(paras.range - 1, 'year').valueOf())
  } else {
    // day
    paras = {
      format: 'YYYY-MM-DD',
      domain: { type: 'month', gutter: 5, label: { text: 'MMM', align: 'start' } },
      subDomain: { type: 'ghDay', width: 13, height: 13, gutter: 5, radius: 3 },
      range: 3,
    }
    startDate = new Date(dayjs(endDay).subtract(paras.range - 1, 'month').valueOf())
    // Add CalendarLabel for day view
    options[2] = [
      CalendarLabel,
      {
        width: 30,
        textAlign: 'start',
        text: () => dayjs.weekdaysShort().map((d: string, i: number) => (i % 2 === 0 ? '' : d)),
        padding: [25, 0, 0, 0],
      },
    ]
  }

  if (isArchive) {
    const postContent = document.querySelector('.post-content')
    const postWidth = postContent ? postContent.clientWidth : 300
    if (postWidth >= 633) {
      paras.range = 12
    } else if (postWidth >= 400) {
      paras.range = 8
    } else if (postWidth >= 300) {
      paras.range = 6
    } else {
      paras.range = 3
    }
    paras.domain.gutter = 2
    paras.subDomain.width = 9
    paras.subDomain.height = 9
    paras.subDomain.gutter = 2
    paras.subDomain.radius = 2
    startDate = new Date(dayjs(endDay).subtract(paras.range - 1, 'month').valueOf())
  }

  // Select data source based on scale
  let sourceData: any[]
  if (by === 'year') {
    sourceData = statsData.calendar.by_year
  } else if (by === 'month') {
    sourceData = statsData.calendar.by_month
  } else {
    sourceData = statsData.calendar.by_day
  }

  console.log('[calendar] painting', selectors.cal, 'by=' + by, 'range=' + paras.range, 'data count=' + sourceData.length)

  cal.paint(
    {
      itemSelector: selectors.cal,
      domain: {
        type: paras.domain.type,
        gutter: paras.domain.gutter,
        label: {
          text: paras.domain.label.text,
          textAlign: paras.domain.label.align,
          position: 'top',
        },
      },
      subDomain: {
        type: paras.subDomain.type,
        gutter: paras.subDomain.gutter,
        width: paras.subDomain.width,
        height: paras.subDomain.height,
        radius: paras.subDomain.radius,
        label: null,
      },
      date: {
        start: startDate,
        max: new Date(endDay),
        highlight: [new Date(endDay)],
      },
      range: paras.range,
      data: {
        source: sourceData,
        x: 'date',
        y: 'count',
      },
      scale: { color: calColor },
    },
    options
  )

  // Bind prev/next navigation
  const prevEl = document.querySelector(selectors.prev)
  const nextEl = document.querySelector(selectors.next)
  if (prevEl) {
    prevEl.addEventListener('click', (e) => {
      e.preventDefault()
      cal.previous()
    })
  }
  if (nextEl) {
    nextEl.addEventListener('click', (e) => {
      e.preventDefault()
      cal.next()
    })
  }
}
